import { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import axiosInstance from "../../config/axios";
import { API_URLS } from "../../config/APIUrls";

const fetchAsBlob = async (url) => {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error("Failed to fetch image");
    return res.blob();
};

const downloadSingleImage = async (img) => {
    try {
        const blob = await fetchAsBlob(img.url);
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = img.name || "image";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
        window.open(img.url, "_blank");
    }
};

const downloadImagesAsZip = async (images, zipName) => {
    const zip = new JSZip();
    let addedCount = 0;

    await Promise.all(
        images.map(async (img, idx) => {
            try {
                const blob = await fetchAsBlob(img.url);
                const ext = img.url.split(".").pop().split("?")[0] || "jpg";
                const name = img.name || `image_${idx + 1}.${ext}`;
                zip.file(name, blob);
                addedCount += 1;
            } catch (err) {
                // skip images that fail to fetch
            }
        })
    );

    if (addedCount === 0) {
        toast.error("Could not download images");
        return;
    }

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `${zipName || "images"}_${Date.now()}.zip`);
};

const PropertyImagesModal = ({ open, onClose, images = [], title, propertyId, onImagesChanged }) => {
    const [selected, setSelected] = useState([]);
    const [preview, setPreview] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const handleDeleteImage = async (img) => {
        if (!propertyId) return;
        setDeletingId(img.id);
        try {
            const res = await axiosInstance.post(API_URLS.update_property_images, {
                id: propertyId,
                remove_images: img.name,
            });
            toast(res?.data?.message);
            if (res.data.success) {
                onImagesChanged && onImagesChanged();
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to delete image");
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        if (open) {
            setSelected([]);
            setPreview(null);
        }
    }, [open, images]);

    const toggleSelect = (id) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const allSelected = images.length > 0 && selected.length === images.length;

    const toggleSelectAll = () => {
        setSelected(allSelected ? [] : images.map((img) => img.id));
    };

    const handleDownloadSelected = async () => {
        const imagesToDownload = images.filter((img) => selected.includes(img.id));
        if (imagesToDownload.length === 0) return;

        setDownloading(true);
        try {
            if (imagesToDownload.length === 1) {
                await downloadSingleImage(imagesToDownload[0]);
            } else {
                await downloadImagesAsZip(imagesToDownload, title);
            }
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadAll = async () => {
        if (images.length === 0) return;
        setDownloading(true);
        try {
            if (images.length === 1) {
                await downloadSingleImage(images[0]);
            } else {
                await downloadImagesAsZip(images, title);
            }
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle className="flex items-center justify-between">
                <span>Property Images {title ? `- ${title}` : ""}</span>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {images.length === 0 ? (
                    <p className="text-center text-gray-500 py-10">
                        No images found for this property
                    </p>
                ) : (
                    <>
                        <div className="flex items-center gap-2 mb-3">
                            <Checkbox
                                checked={allSelected}
                                indeterminate={selected.length > 0 && !allSelected}
                                onChange={toggleSelectAll}
                            />
                            <span>Select All ({selected.length}/{images.length})</span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {images.map((img) => (
                                <div
                                    key={img.id}
                                    className="relative border rounded-md overflow-hidden group"
                                >
                                    <Checkbox
                                        checked={selected.includes(img.id)}
                                        onChange={() => toggleSelect(img.id)}
                                        className="!absolute top-0 left-0 z-10 !bg-white/70"
                                        size="small"
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => downloadSingleImage(img)}
                                        className="!absolute top-0 right-0 z-10 !bg-white/70"
                                        title="Download this image"
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                    {propertyId && (
                                        <IconButton
                                            size="small"
                                            disabled={deletingId === img.id}
                                            onClick={() => handleDeleteImage(img)}
                                            className="!absolute bottom-0 right-0 z-10 !bg-white/70"
                                            title="Delete this image"
                                        >
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    )}
                                    <img
                                        src={img.url}
                                        alt={img.name}
                                        onClick={() => setPreview(img)}
                                        className="w-full h-32 object-cover cursor-pointer"
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {preview && (
                    <div
                        className="fixed inset-0 z-[1400] bg-black/80 flex items-center justify-center"
                        onClick={() => setPreview(null)}
                    >
                        <img
                            src={preview.url}
                            alt={preview.name}
                            className="max-h-[85vh] max-w-[90vw] object-contain"
                        />
                    </div>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="outlined"
                    disabled={selected.length === 0 || downloading}
                    onClick={handleDownloadSelected}
                >
                    Download Selected ({selected.length})
                </Button>
                <Button
                    variant="contained"
                    disabled={images.length === 0 || downloading}
                    onClick={handleDownloadAll}
                >
                    {downloading ? "Downloading..." : "Download All"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PropertyImagesModal;
